// El caché del modelo, en la pestaña Gasto (Pau 08-10-2026: «ver el cache hit rate que estamos teniendo»; él mismo
// eligió ponerlo aquí y no en el Pulso: es dinero). De cada 100 tokens que Calypso manda al modelo en las
// conversaciones, cuántos salen del caché (mucho más baratos), frente al periodo anterior y al objetivo del 95 %
// (cambio «caché al 95 %», Venuz #442), con su línea por día y el reparto por modelo.
import { useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { api } from '../../../lib/api';
import { delta, numero, SIN_VALOR } from './formato';
import { HoverDia, LineaDia } from './GraficosDia';
import type { CacheSala } from './tipos';

const pct = (v: number | null) => (v === null ? SIN_VALOR : `${numero(v, 1)} %`);

export function CacheGasto({ desde, hasta }: { desde: string; hasta: string }) {
  const [foto, setFoto] = useState<{ clave: string; datos: CacheSala | null } | null>(null);
  const clave = `${desde}|${hasta}`;
  useEffect(() => {
    let vivo = true;
    api<CacheSala>(`/api/sala/gasto/cache?desde=${desde}&hasta=${hasta}`)
      .then((datos) => vivo && setFoto({ clave: `${desde}|${hasta}`, datos }))
      .catch(() => vivo && setFoto({ clave: `${desde}|${hasta}`, datos: null }));
    return () => {
      vivo = false;
    };
  }, [desde, hasta]);
  const actual = foto?.clave === clave ? foto : null;
  if (!actual) return null;
  const c = actual.datos;
  if (!c) return <p className="rounded-o-m bg-oficina-elevada px-5 py-3 text-o-sub text-oficina-texto3">{t('salaControl.cache.error')}</p>;
  const valor = c.periodo.conversacion;
  const d = delta(valor, c.anterior?.conversacion ?? null, true);
  const falta = valor === null ? null : c.objetivo - valor;
  const variosDias = c.porDia.length > 1;
  return (
    <section aria-label={t('salaControl.cache.titulo')} className="grid grid-cols-1 gap-3 rounded-o-m bg-oficina-elevada px-5 py-4 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
      <div>
        <h3 className="text-o-texto font-semibold">{t('salaControl.cache.titulo')}</h3>
        <p className="text-o-nota text-oficina-texto3">{t('salaControl.cache.sub')}</p>
        <p className="mt-2 text-o-cifra font-bold leading-none tracking-tight">{pct(valor)}</p>
        <p className="mt-1.5 text-o-sub text-oficina-texto3">
          {c.anterior ? (
            <>
              <span className={cn(d?.tono === 'bueno' ? 'text-oficina-trabajando' : d?.tono === 'malo' ? 'text-oficina-error' : '')}>
                {d && d.direccion !== 'igual' ? `${d.direccion === 'sube' ? '↑' : '↓'} ${d.texto}` : t('salaControl.igual')}
              </span>{' '}
              {t('salaControl.cache.vsAnterior', { valor: pct(c.anterior.conversacion) })}
            </>
          ) : null}
        </p>
        <div className="mt-3" aria-label={t('salaControl.cache.objetivo', { n: c.objetivo })}>
          <div className="relative h-2 rounded-full bg-oficina-hover">
            <div className="absolute inset-y-0 left-0 rounded-full bg-oficina-te-necesita" style={{ width: `${Math.max(0, Math.min(100, valor ?? 0))}%` }} />
            <div className="absolute inset-y-[-3px] w-0.5 bg-oficina-texto" style={{ left: `${c.objetivo}%` }} />
          </div>
          <p className="mt-1 text-o-nota text-oficina-texto3">
            {falta !== null && falta > 0 ? t('salaControl.cache.faltan', { n: numero(falta, 1), objetivo: c.objetivo }) : t('salaControl.cache.objetivo', { n: c.objetivo })}
          </p>
        </div>
        {c.periodo.modelos.length ? (
          <ul className="mt-3 space-y-0.5 text-o-nota text-oficina-texto2">
            {c.periodo.modelos.map((m) => (
              <li key={m.modelo} className="flex justify-between gap-3">
                <span className="truncate">{m.modelo}</span>
                <span className="tabular-nums">{pct(m.tasa)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {c.faltan > 0 ? <p className="mt-2 text-o-nota text-oficina-te-necesita-claro">{t('salaControl.cache.rellenando', { n: c.faltan })}</p> : null}
        {c.periodo.aparte !== null ? <p className="mt-2 text-o-nota text-oficina-texto3">{t('salaControl.cache.aparte', { valor: pct(c.periodo.aparte) })}</p> : null}
      </div>
      {variosDias ? (
        <div className="flex min-h-[180px] flex-col">
          <p className="text-o-nota text-oficina-texto3">{t('salaControl.cache.porDia', { n: c.objetivo })}</p>
          <div className="min-h-0 flex-1">
            <HoverDia>
              <LineaDia
                dias={c.porDia.map((x) => x.dia)}
                valores={c.porDia.map((x) => x.conversacion)}
                comparado={c.porDia.map(() => c.objetivo)}
                comparadoTexto={t('salaControl.grafica.objetivo')}
                formato={(v) => pct(v)}
                formatoEje={(v) => `${numero(v)} %`}
                etiqueta={t('salaControl.cache.titulo')}
              />
            </HoverDia>
          </div>
        </div>
      ) : null}
    </section>
  );
}
